/**
 * Halloween doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenDoodlesPack: ToilePack = {
    slug: 'halloween-doodles',
    category: 'season',
    name: 'Halloween doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mixed', label: 'Mixed doodles', subject: 'A page packed with small halloween doodles: a bat, a candy, a potion, a bone, a web, a moon, a cat, no gaps'},
        {key: 'candy', label: 'Candy doodles', subject: 'A page of small candy doodles: wrapped sweets, lollipops, candy corn, a bucket, a chocolate bar, packed tight'},
        {key: 'spooky', label: 'Spooky doodles', subject: 'A page of spooky doodles: a skull, a tombstone, a crow, a cauldron, a broom, a hand, packed tight'},
    ],
    styles: [
        {key: 'bold', label: 'Bold doodles', style: 'bold marker-doodle outline, thick confident uniform strokes', palette: 'Black ink only', framing: 'Packed edge to edge across the page with a thin white margin, no gaps between doodles'},
        {key: 'fine', label: 'Fine line doodles', style: 'fine pen doodle line, thin delicate uniform strokes', palette: 'Black ink only', framing: 'Neatly spaced across the page with a small white margin'},
        {key: 'bubble', label: 'Bubble doodles', style: 'chunky bubble-outline doodle line, thick rounded uniform strokes with soft corners', palette: 'Black ink only', framing: 'Arranged in a loose grid across the page with a white margin'},
    ],
    directions: ['a single large doodle', 'in a bullet-journal grid', 'as a sticker sheet'],
    directionHint: 'try: a single large doodle instead',
    seo: {
        title: 'Halloween Doodle Art: Candy, Bats, Potion Bottle',
        metaDescription: 'Nine halloween doodle sheets — candy, bats, a potion bottle, a spider, a bone — packed edge to edge, easy hand-drawn style, three styles.',
        h1: 'Halloween doodle art: candy, bats, potion bottle',
        tagline: 'Nine sheets, three October subjects, three styles each.',
        intro: "Pages of small, hand-drawn-style doodles packed edge to edge: candy, bats, a potion bottle, a spider, a bone, built loose and easy. Ask for a jack-o'-lantern, a ghost, or your own subject, and the same three styles carry it.",
        useCases: [
            'a bullet journal spread',
            'a handmade Halloween card',
            'a sticker sheet layout',
            'a scrapbook or planner page',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine doodle sheets — candy, bats, a potion bottle, a spider, a bone, packed edge to edge — in three styles.'},
            {q: 'Are these easy enough to trace or copy by hand?', a: 'Yes — the style is deliberately simple and loose, closer to hand-drawn doodles than detailed illustration.'},
            {q: 'Can I cut individual doodles out for a sticker sheet?', a: 'Yes — each sheet packs several small doodles you can crop or trace individually.'},
            {q: "Can I get a different subject, like a jack-o'-lantern or ghost?", a: "Yes — the direction field takes any Halloween subject, including a jack-o'-lantern or a ghost."},
            {q: "Can I use these in a product I'm selling, like printed stickers?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
